// Webrtc plugin.
import { trackActions, deviceActions } from '../actions'
import { getDevices, getMediaById, getTrackById } from '../selectors'

import { logManager, API_LOG_TAG } from '../../../logs'

const log = logManager.getLogger('MEDIA')

/**
 * Media related APIs.
 * @method mediaAPI
 * @param  {Function} dispatch Redux dispatch.
 * @param  {Function} getState Redux getState.
 * @return {Object}
 */
export default function mediaAPI ({ dispatch, getState }) {
  return {
    /**
     * Retrieves the available media devices for use.
     *
     * The {@link media.event:devices:change devices:change} event will be
     *    emitted when the available media devices have changed.
     *
     * @public
     * @static
     * @memberof media
     * @method getDevices
     * @return {Object} The lists of camera, microphone, and speaker devices.
     */
    getDevices () {
      log.debug(API_LOG_TAG + 'media.getDevices')
      return getDevices(getState())
    },

    /**
     * Retrieves an available Media object with a specific Media ID.
     * @public
     * @static
     * @memberof media
     * @method getById
     * @param  {string} mediaId The ID of the Media to retrieve.
     * @return {call.MediaObject} A Media object.
     */
    getById (mediaId) {
      log.debug(API_LOG_TAG + 'media.getById: ', mediaId)
      return getMediaById(getState(), mediaId)
    },

    /**
     * Retrieve an available Track object with a specific Track ID.
     * @public
     * @static
     * @memberof media
     * @method getTrackById
     * @param  {string} trackId The ID of the Track to retrieve.
     * @return {call.TrackObject} A Track object.
     */
    getTrackById (trackId) {
      log.debug(API_LOG_TAG + 'media.getTrackById: ', trackId)
      return getTrackById(getState(), trackId)
    },

    /**
     * Requests permission to access media devices on the end-user's machine.
     *
     * This API will trigger the browser to ask the end-user for permission to
     *    access their camera and/or microphone. These permissions are
     *    needed for the SDK to read information about the devices (the label,
     *    for example) and for using the devices for a call.
     *
     * If the browser does not yet have permission, it will prompt the end-user
     *    with a small pop-up window, giving the user a chance to allow/deny the
     *    permissions. The behaviour of this pop-up window differs slightly
     *    based on the browser; it may automatically save the user's decision
     *    (such as in Chrome and Safari) or it may require the user to choose
     *    whether their decision should be saved (such as in Firefox).
     *
     * This API is not required for proper usage of media and/or calls, but
     *    helps to prepare a user before a call is made or received. It allows
     *    an application to prompt the user for device permissions when it is
     *    convenient for them, rather than during call setup. If the user saves
     *    their decision, they will not be prompted again when the SDK accesses
     *    those devices for a call.
     *
     * For device information, the {@link media.getDevices} API will retrieve
     *    the list of media devices available for the SDK to use. If this list
     *    is empty, or is missing information, it is likely that the browser
     *    does not have permission to access the device's information. We
     *    recommend using the {@link media.initializeDevices} API in this
     *    scenario if you would like to allow the end-user to select which
     *    device(s) they would like to use when they make a call, rather than
     *    using the system default.
     *
     * The SDK will emit a {@link media.event:devices:change devices:change}
     *    event when the operation is successful or a
     *    {@link media.event:devices:error devices:error} event if an error is
     *    encountered.
     * @public
     * @static
     * @memberof media
     * @method initializeDevices
     * @param {Object} [constraints]
     * @param {boolean} [constraints.audio=true] Whether to ask for audio device permissions.
     * @param {boolean} [constraints.video=true] Whether to ask for video device permissions.
     * @example
     * // The SDK will ask for both audio and video permissions by default.
     * client.media.initializeDevices()
     *
     * // The SDK will only ask for audio permissions.
     * client.media.initializeDevices({ audio: true, video: false })
     */
    initializeDevices (constraints) {
      log.debug(API_LOG_TAG + 'media.initializeDevices: ', constraints)
      const browserConstraints = {
        audio: constraints === undefined ? true : constraints.audio,
        video: constraints === undefined ? true : constraints.video
      }
      dispatch(deviceActions.initializeDevice(browserConstraints))
    },
    /**
     * Render Media Tracks in a container.
     *
     * The container is specified by providing a CSS selector string that
     *    corresponds to the HTMLElement to act as the container.
     * @public
     * @static
     * @memberof media
     * @method renderTracks
     * @param  {Array<string>} trackIds List of Track IDs to be rendered.
     * @param  {string} cssSelector A CSS selector string that uniquely
     *    identifies an element. Ensure that special characters are properly
     *    escaped.
     * @param  {Object} [options] Additional options for rendering the tracks.
     * @param  {string} [options.speakerId] The speaker's Device ID to use for audio tracks.
     * @example
     * // When a Call receives a new track, render it.
     * client.on('call:newTrack', function (params) {
     *    const track = client.media.getTrackById(params.trackId)
     *    const container = params.local ? localContainer : remoteContainer
     *
     *    // Render the Call's new track when it first becomes available.
     *    client.media.renderTracks([ track.trackId ], container)
     * })
     */
    renderTracks (trackIds, cssSelector, options = {}) {
      log.debug(API_LOG_TAG + 'media.renderTracks: ', trackIds, cssSelector, options)
      dispatch(
        trackActions.renderTracks(trackIds, {
          selector: cssSelector,
          ...options
        })
      )
    },

    /**
     * Remove Media Tracks from a container.
     *
     * The container is specified by providing a CSS selector string that
     *    corresponds to the HTMLElement to act as the container.
     * @public
     * @static
     * @memberof media
     * @method removeTracks
     * @param  {Array<string>} trackIds List of Track IDs to stop being rendered.
     * @param  {string} cssSelector A CSS selector string that uniquely
     *    identifies an element. Ensure that special characters are properly
     *    escaped.
     */
    removeTracks (trackIds, cssSelector) {
      log.debug(API_LOG_TAG + 'media.removeTracks: ', trackIds, cssSelector)
      dispatch(trackActions.removeTracks(trackIds, { selector: cssSelector }))
    },

    /**
     * Mutes the specified Tracks.
     *
     * This API prevents the media of the specified Tracks from being rendered. Audio
     *     Tracks will become silent and video Tracks will be a black frame.
     * This does not stop media from being received by those Tracks. The media simply
     *     cannot be used by the application while the Track is muted.
     *
     * If a local Track being sent in a Call is muted, the Track will be
     *    noticeably muted for the remote user. If a remote Track received in a
     *    call is muted, the result will only be noticeable locally.
     *
     * This mute operation acts on those specified Tracks directly.
     * It does not act on the active Call as a whole.
     *
     * The SDK will emit a {@link media.event:media:muted media:muted} event
     *    when a Track has been muted.
     * @public
     * @static
     * @memberof media
     * @method muteTracks
     * @param  {Array<string>} trackIds List of Track IDs.
     */
    muteTracks (trackIds) {
      log.debug(API_LOG_TAG + 'media.muteTracks: ', trackIds)
      dispatch(trackActions.muteTracks(trackIds))
    },

    /**
     * Unmutes the specified Tracks.
     *
     * Media will resume its normal rendering for the Tracks.
     * Like the 'muteTracks' API, this unmute operation acts on those specified Tracks directly.
     * Therefore it does not act on active Call as a whole.
     *
     * The SDK will emit a {@link media.event:media:unmuted media:unmuted} event
     *    when a Track has been unmuted.
     * @public
     * @static
     * @memberof media
     * @method unmuteTracks
     * @param  {Array<string>} trackIds List of Track IDs.
     */
    unmuteTracks (trackIds) {
      log.debug(API_LOG_TAG + 'media.unmuteTracks: ', trackIds)
      dispatch(trackActions.unmuteTracks(trackIds))
    }
  }
}
